import proj4 from "proj4";
import {
  point as turfPoint,
  feature as turfFeature,
  buffer as turfBuffer,
  booleanPointInPolygon,
  booleanIntersects,
} from "@turf/turf";

// Define EPSG:28992 projection
proj4.defs(
  "EPSG:28992",
  "+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 " +
    "+k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel " +
    "+towgs84=565.2369,50.0087,465.658,0.406857330322398," +
    "0.350732676542563,-1.8703473836068,4.0812 +units=m +no_defs"
);

// WFS service URLs
const RCE_WFS_URL = "https://services.rce.geovoorziening.nl/rce/wfs";
const BAG_WFS_URL = "https://service.pdok.nl/lv/bag/wfs/v2_0";

// Monument layers to check
const monumentLayers = [
  "rce:NationalListedMonumentPoints",
  "rce:NationalListedMonumentPolygons",
  "rce:Townscapes",
];

// CRS conversions
function toWGS84([x, y]) {
  return proj4("EPSG:28992", "EPSG:4326", [x, y]);
}

function toRDNew([lon, lat]) {
  return proj4("EPSG:4326", "EPSG:28992", [lon, lat]);
}

// Main function to determine if an address is a monument
export async function isAddressMonument(rdX, rdY) {
  const bufferDistance = 1;
  const pointRD = turfPoint([rdX, rdY]);

  for (const layer of monumentLayers) {
    const url = new URL(RCE_WFS_URL);
    url.searchParams.set("service", "WFS");
    url.searchParams.set("version", "1.1.0");
    url.searchParams.set("request", "GetFeature");
    url.searchParams.set("typename", layer);
    url.searchParams.set("outputFormat", "application/json");
    url.searchParams.set("srsName", "EPSG:28992");

    const bbox = [
      rdX - bufferDistance,
      rdY - bufferDistance,
      rdX + bufferDistance,
      rdY + bufferDistance,
    ].join(",");
    url.searchParams.set("bbox", `${bbox},EPSG:28992`);

    try {
      const res = await fetch(url.toString());
      const geojson = await res.json();

      if (!geojson?.features?.length) continue;

      if (layer === "rce:NationalListedMonumentPoints") {
        const building = await fetchBagBuildingPolygon(rdX, rdY);
        if (!building) continue;

        for (const feature of geojson.features) {
          const monumentCoords = feature.geometry.coordinates;
          const monumentWGS = toWGS84(monumentCoords);
          const monumentPoint = turfPoint(monumentWGS);
          const monumentBufferWGS = turfBuffer(monumentPoint, 1, {
            units: "meters",
          });

          const bufferRDCoords = monumentBufferWGS.geometry.coordinates.map(
            (ring) => ring.map(toRDNew)
          );
          const bufferRD = turfFeature({
            type: "Polygon",
            coordinates: bufferRDCoords,
          });

          // console.log("Building:", building.geometry.coordinates);
          // console.log("Buffer RD:", bufferRD.geometry.coordinates);

          if (booleanIntersects(building, bufferRD)) {
            return true;
          }
        }
      } else {
        for (const feature of geojson.features) {
          const geometry = feature.geometry;

          if (geometry.type === "Polygon" || geometry.type === "MultiPolygon") {
            const polygon = turfFeature(geometry);
            if (booleanPointInPolygon(pointRD, polygon)) {
              return true;
            }
          }

          if (geometry.type === "Point") {
            const [fx, fy] = geometry.coordinates;
            if (
              Math.abs(fx - rdX) < bufferDistance &&
              Math.abs(fy - rdY) < bufferDistance
            ) {
              return true;
            }
          }
        }
      }
    } catch (err) {
      console.error(`Error checking layer ${layer}:`, err);
    }
  }

  return false;
}

// Function to fetch the BAG building polygon for given RD coordinates
async function fetchBagBuildingPolygon(rdX, rdY) {
  const bufferSize = 0.5;
  const bbox = [
    rdX - bufferSize,
    rdY - bufferSize,
    rdX + bufferSize,
    rdY + bufferSize,
  ].join(",");

  const url = new URL(BAG_WFS_URL);
  url.searchParams.set("service", "WFS");
  url.searchParams.set("version", "2.0.0");
  url.searchParams.set("request", "GetFeature");
  url.searchParams.set("typeNames", "bag:pand");
  url.searchParams.set("outputFormat", "application/json");
  url.searchParams.set("srsName", "EPSG:28992");
  url.searchParams.set("bbox", `${bbox},EPSG:28992`);

  try {
    const res = await fetch(url.toString());
    const geojson = await res.json();

    if (geojson?.features?.length > 0) {
      return turfFeature(geojson.features[0].geometry);
    }
  } catch (err) {
    console.error("Error fetching BAG building polygon:", err);
  }

  return null;
}
